import Button from '../button/Button';
import { withPersonalizedButtonStyle } from '../button/withPersonalizedButtonStyle';
import { styles } from './PickerSourceButton.styles';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { PickerSourceIcon } from './picker-source-icon/PickerSourceIcon';
import { PickerSourceProps } from './PickerSourceButton.types';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { BUTTON_TEXT_POSITIONS } from '../button/Button.types.constants';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';

const PickerSource = observer(
  ({
    source,
    isSelected,
    style,
    innerStyle,
    textStyle,
    buttonSelectedStyle,
    textSelectedStyle,
    pickerStyle,
    pickerFocusStyle,
    pickerDisabledStyle,
    pickerHoverStyle,
    pickerPressedStyle,
    pickerSelectedStyle,
    hintDisabled,
    ...buttonProps
  }: PickerSourceProps) => {
    const { i18n } = useStore();
    const { isDesktop } = useResponsiveScreen();

    return (
      <Button
        {...buttonProps}
        style={mergeStyles(
          isDesktop ? styles.buttonDesktop : styles.buttonMobile,
          style,
          isSelected && mergeStyles(styles.buttonSelected, buttonSelectedStyle),
          buttonProps.disabled && ({ cursor: 'not-allowed' } as any),
        )}
        innerStyle={mergeStyles(isDesktop ? styles.buttonInnerDesktop : styles.buttonInnerMobile, innerStyle)}
        textStyle={mergeStyles(
          isDesktop ? styles.text : styles.textMobile,
          textStyle,
          isSelected && mergeStyles(styles.textSelected, textSelectedStyle),
        )}
        accessible={true}
        accessibilityLabel={i18n.t(`actions:open_x`, { x: i18n.t(`upload:${source}`) })}
        accessibilityRole="button"
        textPosition={BUTTON_TEXT_POSITIONS.RIGHT}
      >
        {({ focused, disabled, hovered, pressed }) => {
          let color = pickerStyle?.color ?? styles.picker.color;
          if (focused) {
            color = pickerFocusStyle?.color ?? styles.pickerFocus.color;
          } else if (disabled) {
            color = pickerDisabledStyle?.color ?? styles.pickerDisabled.color;
          } else if (hovered) {
            color = pickerHoverStyle?.color ?? styles.pickerHover.color;
          } else if (pressed) {
            color = pickerPressedStyle?.color ?? styles.pickerPressed.color;
          } else if (isSelected) {
            color = pickerSelectedStyle?.color ?? styles.pickerSelected.color;
          }
          return (
            <>
              <PickerSourceIcon color={color} type={source} filled={isSelected} />
              {hovered && disabled && (
                <Animated.View
                  entering={FadeIn.duration(200)}
                  exiting={FadeOut.duration(200)}
                  style={styles.hoverTextContainer}
                  pointerEvents="none"
                >
                  <TextLato style={styles.hoverText}>{hintDisabled}</TextLato>
                </Animated.View>
              )}
            </>
          );
        }}
      </Button>
    );
  },
);

export const PickerSourceButton = withPersonalizedButtonStyle<PickerSourceProps>(PickerSource, styles);
